import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { OutlineArrowRight } from '@producer/ui-kit';

import { useResponsive } from '../../../hooks/useResponsive';
import { SettingsMenu } from './settings-menu';

import styles from './settings-page-layout.module.scss';
type MenuOptions = {
  key: string;
  label: string;
  href: string;
};

type SettingsLayoutProps = React.PropsWithChildren<{
  title: string;
  menu: MenuOptions[];
  displayBackButton?: boolean;
}>;

export const SettingsPageLayout: React.FC<SettingsLayoutProps> = ({
  children,
  title,
  menu,
  displayBackButton,
}) => {
  const navigate = useNavigate();
  const { spaceId } = useParams() as { spaceId: string };
  const location = useLocation() as { state: { prevPath: string } };
  const { isMobile } = useResponsive();

  const goBack = () => {
    if (location.state && location.state.prevPath) {
      navigate(location.state.prevPath);
    } else if (spaceId) {
      navigate(`/space/${spaceId}`);
    } else {
      navigate(`/`);
    }
  };

  return (
    <Wrapper>
      {!isMobile && (
        <NavigationBar>
          <Header>
            {displayBackButton && (
              <BackButton onClick={goBack}>
                <OutlineArrowRight />

                <span>Back</span>
              </BackButton>
            )}

            <SettingsTitle>{title}</SettingsTitle>
          </Header>

          <MenuContainer>
            <SettingsMenu items={menu} />
          </MenuContainer>
        </NavigationBar>
      )}

      <Screens>{children}</Screens>
    </Wrapper>
  );
};

const Wrapper = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['wrapper']} ${className}` : styles['wrapper']} {...props} />
);

const NavigationBar = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['navigation-bar']} ${className}` : styles['navigation-bar']}
    {...props}
  />
);

const Screens = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['screens']} ${className}` : styles['screens']} {...props} />
);

const MenuContainer = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['menu-container']} ${className}` : styles['menu-container']}
    {...props}
  />
);

const SettingsTitle = ({ className, ...props }: React.ComponentPropsWithoutRef<'h1'>) => (
  <h1
    className={className ? `${styles['settings-title']} ${className}` : styles['settings-title']}
    {...props}
  />
);

const Header = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['header']} ${className}` : styles['header']} {...props} />
);

export const BackButton = ({ className, ...props }: React.ComponentPropsWithoutRef<'a'>) => (
  <a
    className={className ? `${styles['back-button']} ${className}` : styles['back-button']}
    {...props}
  />
);
